import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { isUserLoggedIn } from "hooks/LocalStorage";

import {
  flush,
  setAnalyticsEnabled,
  startNewSession,
  track,
  trackHeartbeat,
  trackScreen,
} from "./tracker";

const HEARTBEAT_INTERVAL_MS = 45_000;

function tabIsVisible() {
  return typeof document === "undefined" || document.visibilityState === "visible";
}

/**
 * Headless tracker for Satyam Tours web: sessions, page views, and presence.
 * Mounted once inside the router so it can see navigation + login state.
 */
export default function AnalyticsTracker() {
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const visitOpen = useRef(false);
  const authenticatedRef = useRef(isUserLoggedIn());
  const heartbeatTimer = useRef(null);

  const stopHeartbeat = () => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
  };

  const startHeartbeat = () => {
    stopHeartbeat();
    trackHeartbeat();
    heartbeatTimer.current = setInterval(() => {
      if (visitOpen.current && authenticatedRef.current && tabIsVisible()) {
        trackHeartbeat();
      }
    }, HEARTBEAT_INTERVAL_MS);
  };

  const openVisit = () => {
    startNewSession();
    setAnalyticsEnabled(true);
    track("app_open", "lifecycle");
    visitOpen.current = true;
    startHeartbeat();
  };

  const closeVisit = () => {
    if (!visitOpen.current) {
      return;
    }
    stopHeartbeat();
    track("app_background", "lifecycle");
    visitOpen.current = false;
    void flush();
  };

  const syncAuth = () => {
    const loggedIn = isUserLoggedIn();
    authenticatedRef.current = loggedIn;

    if (loggedIn) {
      if (tabIsVisible() && !visitOpen.current) {
        openVisit();
        if (pathnameRef.current) {
          trackScreen(pathnameRef.current);
        }
      } else if (!visitOpen.current) {
        setAnalyticsEnabled(true);
      }
    } else {
      stopHeartbeat();
      visitOpen.current = false;
      setAnalyticsEnabled(false);
    }
  };

  useEffect(() => {
    syncAuth();
    window.addEventListener("storage", syncAuth);
    return () => {
      window.removeEventListener("storage", syncAuth);
    };
  }, []);

  useEffect(() => {
    if (authenticatedRef.current && pathname && visitOpen.current) {
      trackScreen(pathname);
    }
  }, [pathname]);

  useEffect(() => {
    const onVisibility = () => {
      if (!authenticatedRef.current) {
        return;
      }
      if (tabIsVisible()) {
        if (!visitOpen.current) {
          openVisit();
          if (pathname) {
            trackScreen(pathname);
          }
        }
        return;
      }
      closeVisit();
    };

    const onPageHide = () => {
      if (authenticatedRef.current) {
        closeVisit();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      stopHeartbeat();
    };
  }, [pathname]);

  return null;
}
